import React, { useMemo, useState } from 'react';
import { useTableInstance, getCoreRowModel } from '@tanstack/react-table';
import BarLoader from 'react-bar-loader';
import { useQuery } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {
  faSort,
  faSortAsc,
  faSortDesc,
} from '@fortawesome/free-solid-svg-icons';
import FilterSidebar from '../FilterSidebar';
import {
  AddSvg,
  FileExport,
  FilterSvg,
  RefreshSvg,
  UpdateSvg,
  UploadSvg,
} from '../../assets/images/Images';
import LoaderSvg from '../Loader/LoaderSvg';

const pageNumberListLimit = 5;

const TableManager = ({
  filter,
  fetcherFn,
  fetcherKey,
  columns,
  table,
  title,
  ctaFunction,
  ctaLabel,
  cta = false,
  shouldFilter = false,
  openFilter,
  setOpenFilter,
  initialValues,
  setFilters,
  filters,
  shouldExport = false,
  exportCtaFn,
  isExporting = false,

  shouldUpload = false,
  uploadCtaFunction,
  uploadCtaLabel,

  updateMargin = false,
  updateMarginCtaFunction,
  updateMarginCtaLabel,
  uploadFile,
  uploadLiveUsersFunction,
  liveNumberUploadFile,
  uploadLiveNumberFunction,
}) => {
  const [data, setData] = useState({});
  const memoizedColumns = useMemo(() => columns, [columns]);
  const [sorting, setSorting] = useState([]);
  const [maxPageNumberListLimit, setMaxPageNumberListLimit] = useState(5);
  const [minPageNumberListLimit, setMinPageNumberListLimit] = useState(0);
  const memoizedSorting = useMemo(() => sorting, [sorting]);
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  );
  const {
    data: tableDataList,
    refetch,
    isLoading,
    isFetching,
  } = useQuery(
    [
      fetcherKey,
      {
        filter:
          filter?.map(
            ({
              placeholder,
              type,
              shouldFilterOptionDisabled,
              filterOptions,
              selectOptions,
              label,
              ...rest
            }) => ({ ...rest }),
          ) ?? [],
        page: pageIndex + 1,
        limit: pageSize,
        sort: memoizedSorting[0] || {},
      },
    ],
    ({ queryKey }) => fetcherFn(queryKey[1] || {}),
    {
      onSuccess: (data) => {
        const tableData = data?.data || {};
        setData(tableData);
      },
      onError: () => {
        setData({});
      },
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );
  const tableInstance = useTableInstance(table, {
    debugAll: false,
    debugTable: false,
    data: tableDataList?.data?.data || [],
    columns: memoizedColumns || [],
    state: {
      pagination,
      sorting: memoizedSorting,
    },
    manualPagination: true,
    manualSorting: true,
    pageCount: data?.lastPage ?? -1,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
  });
  const handleNextPage = () => {
    tableInstance?.nextPage();
    if (pageIndex === tableInstance?.getPageCount() - 1) {
      return false;
    } else {
      if (pageIndex + 2 > maxPageNumberListLimit) {
        setMaxPageNumberListLimit(maxPageNumberListLimit + pageNumberListLimit);
        setMinPageNumberListLimit(minPageNumberListLimit + pageNumberListLimit);
      }
    }
  };
  const handlePreviousPage = (e) => {
    tableInstance?.previousPage();
    if (pageIndex === 0) {
      return;
    } else {
      if (pageIndex % pageNumberListLimit === 0) {
        setMaxPageNumberListLimit(maxPageNumberListLimit - pageNumberListLimit);
        setMinPageNumberListLimit(minPageNumberListLimit - pageNumberListLimit);
      }
    }
  };
  const handleLastPage = () => {
    tableInstance?.setPageIndex(tableInstance?.getPageCount() - 1);
    const min = tableInstance.getPageCount() - 5;
    const max = tableInstance.getPageCount();

    setMaxPageNumberListLimit(max);
    setMinPageNumberListLimit(min);
  };
  const handleFirstPage = () => {
    tableInstance?.setPageIndex(0);
    const min = 0;
    const max = 5;
    setMaxPageNumberListLimit(max);
    setMinPageNumberListLimit(min);
  };

  return (
    <>
      <div className="page_content">
        <section className="lower_section pt-2">
          <div className="page_heading page_heading_enhence d-flex justify-content-between align-items-center w-100 bg-white p-2">
            <h3 className="d-flex align-items-center">
              <span className="title">{title || 'Listing'}</span>{' '}
              {tableDataList?.data ? (
                <>
                  <span className="font-15 ml-1 mr-2  text-black">|</span>
                  <span className="mr-3 d-flex wrapSpan font-13 font-500 font-black d-flex align-items-center">
                    Total{' '}
                    <span className="btn btn-primary reload_icon page_count_btn font-600">
                      {tableDataList?.data?.totalRecords}{' '}
                    </span>
                    records
                  </span>
                </>
              ) : null}
            </h3>
            <div className="d-flex gutter-1">
              {uploadFile ? (
                <OverlayTrigger
                  key="cta-btn-upload"
                  placement="top"
                  overlay={<Tooltip id={`tooltip-top`}>Upload File</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center justify-content-center  reload_icon"
                    onClick={() => uploadLiveUsersFunction()}
                  >
                    <UploadSvg />
                  </button>
                </OverlayTrigger>
              ) : null}

              {liveNumberUploadFile ? (
                <OverlayTrigger
                  key="cta-btn-live-number"
                  placement="top"
                  overlay={<Tooltip id={`tooltip-top`}>Upload File</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center justify-content-center  reload_icon"
                    onClick={() => uploadLiveNumberFunction()}
                  >
                    <UploadSvg />
                  </button>
                </OverlayTrigger>
              ) : null}

              {cta ? (
                <OverlayTrigger
                  key="cta-btn"
                  placement="top"
                  overlay={<Tooltip id={`tooltip-top`}>{ctaLabel}</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center justify-content-center  reload_icon"
                    onClick={() => ctaFunction()}
                  >
                    <AddSvg />
                  </button>
                </OverlayTrigger>
              ) : null}
              {shouldUpload ? (
                <OverlayTrigger
                  key="cta-upload-btn"
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-top`}>{uploadCtaLabel}</Tooltip>
                  }
                >
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center justify-content-center  reload_icon"
                    onClick={() => uploadCtaFunction()}
                  >
                    <UploadSvg />
                  </button>
                </OverlayTrigger>
              ) : null}

              {updateMargin ? (
                <OverlayTrigger
                  key="cta-update-margin-btn"
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-top`}>{updateMarginCtaLabel}</Tooltip>
                  }
                >
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center justify-content-center  reload_icon"
                    onClick={() => updateMarginCtaFunction()}
                  >
                    <UpdateSvg />
                  </button>
                </OverlayTrigger>
              ) : null}

              {shouldFilter ? (
                <OverlayTrigger
                  key="filter-btn"
                  placement="top"
                  overlay={<Tooltip id={`tooltip-top`}>Filter</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center justify-content-center  reload_icon position-relative"
                    onClick={() => setOpenFilter(!openFilter)}
                  >
                    <FilterSvg />
                    {filters && filters.length > 0 ? (
                      <span className="filter_count"> {filters.length} </span>
                    ) : null}
                  </button>
                </OverlayTrigger>
              ) : null}

              {shouldExport ? (
                <OverlayTrigger
                  key="export-btn"
                  placement="top"
                  overlay={<Tooltip id={`tooltip-top`}>Export File</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center justify-content-center  reload_icon"
                    onClick={() =>
                      exportCtaFn({
                        filter:
                          filter?.map(
                            ({
                              placeholder,
                              type,
                              shouldFilterOptionDisabled,
                              filterOptions,
                              selectOptions,
                              label,
                              ...rest
                            }) => ({ ...rest }),
                          ) ?? [],
                        page: filters && filters.length > 0 ? pageIndex + 1 : 1,
                        limit: pageSize,
                        sort: memoizedSorting[0] || {},
                      })
                    }
                  >
                    {isExporting ? <LoaderSvg /> : <FileExport />}
                  </button>
                </OverlayTrigger>
              ) : null}

              <OverlayTrigger
                key="refresh-btn"
                placement="top"
                overlay={<Tooltip id={`tooltip-top`}>Refresh</Tooltip>}
              >
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center justify-content-center  reload_icon"
                  onClick={() => refetch()}
                >
                  <RefreshSvg />
                </button>
              </OverlayTrigger>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <div className=" table-responsive position-relative">
                {(isLoading || isFetching) && (
                  <div className="custom-linear-loader-row">
                    <div className="custom-linear-loader">
                      <BarLoader color="#000000" height="4px" />
                    </div>
                  </div>
                )}
                <table className="table table_custom table_custom_enhence  mb-0">
                  <thead>
                    {tableInstance?.getHeaderGroups()?.map((headerGroup) => (
                      <tr key={headerGroup?.id}>
                        {headerGroup?.headers?.map((header) => {
                          return (
                            <th
                              key={header?.id}
                              style={{
                                textAlign:
                                  header?.column?.columnDef?.textAlign ??
                                  'center',
                                width:
                                  header?.column?.columnDef?.width ||
                                  `${(
                                    100 / header?.headerGroup?.headers?.length
                                  ).toFixed(2)}%`,
                              }}
                            >
                              {header?.isPlaceholder ? null : header?.column
                                  ?.columnDef?.isSortable ? (
                                <div
                                  className="pointer"
                                  onClick={header?.column?.getToggleSortingHandler()}
                                >
                                  {header?.renderHeader()}{' '}
                                  {{
                                    asc: <FontAwesomeIcon icon={faSortAsc} />,
                                    desc: <FontAwesomeIcon icon={faSortDesc} />,
                                  }[header.column.getIsSorted()] ?? (
                                    <FontAwesomeIcon icon={faSort} />
                                  )}
                                </div>
                              ) : (
                                header?.renderHeader()
                              )}
                            </th>
                          );
                        })}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {tableInstance?.getRowModel()?.rows?.length > 0 ? (
                      tableInstance?.getRowModel()?.rows?.map((row) => (
                        <tr key={row?.id}>
                          {row?.getVisibleCells()?.map((cell) => {
                            return (
                              <td
                                key={cell?.id}
                                style={{
                                  textAlign:
                                    cell?.column?.columnDef?.textAlign ??
                                    'center',
                                  width:
                                    cell?.column?.columnDef?.width ||
                                    `${(
                                      100 / cell?.headerGroup?.headers?.length
                                    ).toFixed(2)}%`,
                                  cursor: cell?.column?.columnDef?.shouldPointer
                                    ? 'pointer'
                                    : '',
                                }}
                              >
                                {cell?.column?.columnDefType === 'display'
                                  ? cell?.renderCell()
                                  : cell?.getValue() || cell?.getValue() == 0
                                  ? cell?.renderCell()
                                  : '---'}
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={tableInstance?.getAllColumns()?.length}
                          align="center"
                        >
                          No Record Found
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot></tfoot>
                </table>

                {!isLoading &&
                  tableInstance?.getRowModel()?.rows?.length > 0 && (
                    <div className="col-md-12 m-0 pagingWrap p-0 d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <span className="mr-3 wrapSpan">
                          Page{' '}
                          <span className="font-600">
                            {tableInstance?.getState()?.pagination?.pageIndex +
                              1}{' '}
                          </span>
                          of{' '}
                          <span className="font-600">
                            {tableInstance?.getPageCount()}
                          </span>
                        </span>

                        <span className="d-flex space-nowrap mr-3 wrapSpan">
                          Jump to{' '}
                          <input
                            className="form-control mx-2"
                            type="number"
                            defaultValue={
                              tableInstance?.getState()?.pagination?.pageIndex +
                              1
                            }
                            onChange={(e) => {
                              const page = e.target.value
                                ? Number(e.target.value) - 1
                                : 0;
                              tableInstance?.setPageIndex(page);
                            }}
                          />
                          page
                        </span>
                        <span className="d-flex space-nowrap mr-3 wrapSpan">
                          Show
                          <select
                            className="form-control mx-2"
                            value={
                              tableInstance?.getState()?.pagination?.pageSize
                            }
                            onChange={(e) =>
                              tableInstance?.setPageSize(Number(e.target.value))
                            }
                          >
                            {[10, 15, 20, 25, 30].map((page, index) => (
                              <option value={page} key={index}>
                                {page}
                              </option>
                            ))}
                          </select>
                          records per page
                        </span>
                      </div>
                      <div className="pagination">
                        <button
                          type="button"
                          disabled={!tableInstance?.getCanPreviousPage()}
                          className={`bg bg--light-white bg--actionbox d-flex align-items-center flex--content-center gutter gutter--mr-s ${
                            !tableInstance?.getCanPreviousPage()
                              ? 'disabled'
                              : ''
                          } `}
                          onClick={() => handleFirstPage()}
                        >
                          <span>&#8249;&#8249;</span>
                        </button>
                        <button
                          type="button"
                          disabled={!tableInstance?.getCanPreviousPage()}
                          className={`bg bg--light-white bg--actionbox d-flex align-items-center flex--content-center gutter gutter--mr-s ${
                            !tableInstance?.getCanPreviousPage()
                              ? 'disabled'
                              : ''
                          } `}
                          onClick={(e) => handlePreviousPage(e)}
                        >
                          <span>&#8249;</span>
                        </button>
                        {tableInstance?.getPageOptions()?.map((page, index) => {
                          const number = page + 1;
                          if (
                            number < maxPageNumberListLimit + 1 &&
                            number > minPageNumberListLimit
                          ) {
                            return (
                              <button
                                type="button"
                                key={index}
                                className={` bg--actionbox d-flex align-items-center flex--content-center gutter gutter--mr-s ${
                                  tableInstance?.getState()?.pagination
                                    ?.pageIndex === index
                                    ? 'active'
                                    : 'bg bg--light-white'
                                }`}
                                onClick={() =>
                                  tableInstance?.setPageIndex(index)
                                }
                              >
                                <span>{page + 1}</span>
                              </button>
                            );
                          } else {
                            return null;
                          }
                        })}
                        <button
                          type="button"
                          disabled={!tableInstance?.getCanNextPage()}
                          className={`bg bg--light-white bg--actionbox d-flex align-items-center flex--content-center gutter gutter--mr-s ${
                            !tableInstance?.getCanNextPage() ? 'disabled' : ''
                          }`}
                          onClick={() => handleNextPage()}
                        >
                          <span>&#8250;</span>
                        </button>
                        <button
                          type="button"
                          disabled={!tableInstance?.getCanNextPage()}
                          className={`bg bg--light-white bg--actionbox d-flex align-items-center flex--content-center gutter gutter--mr-s ${
                            !tableInstance?.getCanNextPage() ? 'disabled' : ''
                          } `}
                          onClick={() => handleLastPage()}
                        >
                          <span>&#8250;&#8250;</span>
                        </button>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </section>

        {shouldFilter && (
          <FilterSidebar
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            initialValues={initialValues}
            setFilters={setFilters}
            filters={filters}
            isSearching={isLoading || isFetching}
          />
        )}
      </div>
    </>
  );
};

export default TableManager;
